import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Snackbar,
    CircularProgress,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";

// Hooks
import { useTranslation } from "@/hooks/useTranslation";
import { useAuth } from "@/hooks/useAuth";
import { useConfig } from "@/hooks/config/useConfig";

// Components
import LayoutCms from "@/theme/layout-cms";
import Icon from "@/theme/vectors/bundle";
import LanguageSettings from "@/components/cms/config/LanguageSettings";
import UserManagement from "@/components/cms/config/UserManagement";
import UserEditPanel from "@/components/cms/config/UserEditPanel";
import DeleteConfirmDialog from "@/components/cms/common/DeleteConfirmDialog";
import AccessDenied from "@/components/cms/common/AccessDenied";

// Create Toast component
const Toast = React.forwardRef(function Toast(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const CmsConfigPage = () => {
    // Get translation and auth hooks
    const { t, language, changeLanguage } = useTranslation();
    const { user, userInfoLoaded, loading: authLoading } = useAuth();
    
    // Config state and custom hook
    const {
        hasAccess,
        initialLoading,
        users,
        roles,
        newUser,
        emailError,
        loading,
        toast,
        slidePanel,
        deleteDialog,
        saveStatus,
        currentUser,
        fetchUsers,
        handleLanguageChange,
        saveLanguagePreference,
        handleInputChange,
        addUser,
        openSlideout,
        closeSlideout,
        handleRoleChange,
        saveUserChanges,
        toggleUserStatus,
        openDeleteDialog,
        closeDeleteDialog,
        confirmDelete,
        handleCloseToast,
    } = useConfig(t, language, user, userInfoLoaded, changeLanguage);
    
    // Loading screen - show while auth is loading or initial loading
    if (authLoading || initialLoading) {
        return (
            <LayoutCms>
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    height: '80vh' 
                }}>
                    <CircularProgress />
                    <Typography variant="h6" sx={{ ml: 2 }}>
                        {t('common.loading')}...
                    </Typography>
                </Box>
            </LayoutCms>
        );
    }

    // No access screen
    if (!hasAccess) {
        return <AccessDenied />;
    }

    return (
        <LayoutCms>
            <Box sx={{ maxWidth: 900, mx: "auto", mt: 2 }}>
                <Typography variant="h4" sx={{ mb: 3, fontWeight: "bold" }}>
                    {t('config.title')}
                </Typography>

                {/* Language Section */}
                <LanguageSettings 
                    language={language}
                    onLanguageChange={handleLanguageChange}
                />

                {/* User Management Section */}
                <UserManagement 
                    users={users}
                    roles={roles}
                    newUser={newUser}
                    emailError={emailError}
                    loading={loading}
                    onInputChange={handleInputChange}
                    onAddUser={addUser}
                    onOpenSlideout={openSlideout}
                />

                {/* Slideout Panel for Editing User */}
                <UserEditPanel
                    isOpen={slidePanel.isOpen}
                    onClose={closeSlideout}
                    currentUser={currentUser}
                    roles={roles}
                    loading={loading}
                    onRoleChange={handleRoleChange}
                    onSave={saveUserChanges}
                    onToggleStatus={toggleUserStatus}
                    onDelete={openDeleteDialog}
                />

                {/* Delete Confirmation Dialog */}
                <DeleteConfirmDialog
                    open={deleteDialog.open}
                    message={t('user.deleteConfirmMessage', { email: deleteDialog.email })}
                    onCancel={closeDeleteDialog}
                    onConfirm={confirmDelete}
                />

                {/* Toast Notification */}
                <Snackbar 
                    open={toast.open} 
                    autoHideDuration={3000} 
                    onClose={handleCloseToast}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                >
                    <Toast onClose={handleCloseToast} severity={toast.severity}>
                        {toast.message}
                    </Toast>
                </Snackbar>
            </Box>
        </LayoutCms>
    );
};

export default CmsConfigPage;